import type { ColumnDef } from '@tanstack/react-table';
import { createActionsColumn, createSerialColumn } from '@/components/common/DataTable';
import { EmptyValue } from '@/components/common/EmptyValue';
import { RowActionButton } from '@/components/common/RowActions';
import { formatDisplayDate } from '@/utils/date';
import { formatLocation } from '@/utils/formatLocation';
import { formatNumber } from '@/utils/number';
import type { SurveyRespondent } from '../types/survey.types';

function latestSubmissionDate(row: SurveyRespondent): string | null {
  let latest: string | null = null;
  for (const submission of row.submissions) {
    if (latest === null || submission.submissionDate > latest) {
      latest = submission.submissionDate;
    }
  }
  return latest;
}

export function getSurveyRespondentColumns(
  onView: (respondent: SurveyRespondent) => void,
): ColumnDef<SurveyRespondent>[] {
  return [
    createSerialColumn<SurveyRespondent>(),
    {
      accessorKey: 'userName',
      header: 'User',
      cell: ({ row }) => <span className="font-medium">{row.original.userName}</span>,
    },
    {
      accessorKey: 'userHrId',
      header: 'HR ID',
      cell: ({ row }) => row.original.userHrId,
    },
    {
      accessorKey: 'contactNumber',
      header: 'Contact',
      cell: ({ row }) => row.original.contactNumber ?? <EmptyValue />,
    },
    {
      id: 'location',
      header: 'Location',
      cell: ({ row }) => (
        <span className="whitespace-nowrap">{formatLocation(row.original) ?? <EmptyValue />}</span>
      ),
    },
    {
      accessorKey: 'departmentName',
      header: 'Department',
      cell: ({ row }) => row.original.departmentName ?? <EmptyValue />,
    },
    {
      id: 'submissionCount',
      header: 'Submissions',
      cell: ({ row }) => (
        <span className="tabular-nums">{formatNumber(row.original.submissions.length)}</span>
      ),
    },
    {
      id: 'latestSubmission',
      header: 'Latest',
      cell: ({ row }) => {
        const latest = latestSubmissionDate(row.original);
        return latest ? formatDisplayDate(latest) : <EmptyValue />;
      },
    },
    createActionsColumn<SurveyRespondent>((respondent) => (
      <RowActionButton
        label="View"
        ariaLabel={`View submissions of ${respondent.userName}`}
        onClick={() => onView(respondent)}
      />
    )),
  ];
}
